import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { flatten, safeParseInt } from 'shared/util'

import { StackProps } from '@mui/material'

import { Loading } from 'components/controls/loading'
import { ErrorBlock } from 'components/controls/errorInfo'
import { useSideMenu } from 'components/sideMenu/container'
import { useTopBar } from 'components/topBar/container'

import { useGetSearchDetail } from 'interface/search'

import { SearchProvider } from 'features/search/context'
import { useGetTargetList } from 'interface/search/targetList'
import { useGetMrpProject, useListMrpLog } from 'interface/carenet/mrp'
import { useGetLvProject, useListLvLog } from 'interface/carenet/lv'

import { SearchGridPage } from './searchPage'
import { TargetGridPage } from './targetPage'
import { useGetEvaluationForm } from 'interface/evaluationForm'

type GridPageProps = {
  searchId: number
  topBarState: ReturnType<typeof useTopBar>
  sideMenuState: ReturnType<typeof useSideMenu>
} & StackProps
export const GridPage = ({
	searchId,
	...others
}: GridPageProps) => {
  const [params] = useSearchParams()
  const targetListId = safeParseInt(params.get('listId')) ?? null
  const mrpProjectId = safeParseInt(params.get('mrp')) ?? null
  const lvProjectCode = params.get('lv') ?? null

	const searchStatus = useGetSearchDetail(searchId)
	const evaluationFormStatus = useGetEvaluationForm(searchStatus.data?.evaluationFormId)
  const targetListStatus = useGetTargetList(searchId, targetListId)
  const mrpProjectStatus = useGetMrpProject(mrpProjectId)
	const mrpLogStatus = useListMrpLog(mrpProjectId)
  const lvProjectStatus = useGetLvProject(lvProjectCode)
	const lvLogStatus = useListLvLog(lvProjectCode)

	const errors = useMemo(
		() => flatten([
			searchStatus.error,
			evaluationFormStatus.error,
			targetListStatus.error,
			mrpProjectStatus.error,
			mrpLogStatus.error,
			lvProjectStatus.error,
			lvLogStatus.error
		]),
		[
			searchStatus.error,
			evaluationFormStatus.error,
			targetListStatus.error,
			mrpProjectStatus.error,
			mrpLogStatus.error,
			lvProjectStatus.error,
			lvLogStatus.error
		]
	)
	if (errors.length > 0) {
		return <ErrorBlock error={errors}/>
	}
	if (searchStatus.loading || evaluationFormStatus.loading) return <Loading/>
	if (!searchStatus.data) return <></>
	if (targetListId || mrpProjectId || lvProjectCode) {
		if (targetListId) {
			if (targetListStatus.loading) return <Loading/>
			if (!targetListStatus.data) return <></>
		}
		if (mrpProjectId) {
			if (mrpProjectStatus.loading || mrpLogStatus.loading) return <Loading/>
			if (!mrpProjectStatus.data) return <></>
		}
		if (lvProjectCode) {
			if (lvProjectStatus.loading || lvLogStatus.loading) return <Loading/>
			if (!lvProjectStatus.data) return <></>
		}
		return (
			<SearchProvider
				search={searchStatus.data}
				evaluationForm={evaluationFormStatus.data}
				targetList={targetListStatus.data}
				mrpProject={mrpProjectStatus.data}
				lvProject={lvProjectStatus.data}
			>
				<TargetGridPage
					search={searchStatus.data}
					targetList={targetListStatus.data}
					mrpProject={mrpProjectStatus.data}
					mrpLogList={mrpLogStatus.data?.list ?? []}
					lvProject={lvProjectStatus.data}
					lvLogList={lvLogStatus.data?.list ?? []}
					{...others}
				/>
			</SearchProvider>
		)
	} else {
  	return (
			<SearchProvider
				search={searchStatus.data}
				evaluationForm={evaluationFormStatus.data}
			>
				<SearchGridPage
					search={searchStatus.data}
					{...others}
				/>
			</SearchProvider>
		)
	}
}
